import { DXFViewer } from '../../src/dxfViewer.js';
import { SnapsHelper } from '../../src/utils/snapsHelper.js';
import { Boilerplate } from '../boilerplate.js';
import GUI from 'lil-gui';
import * as THREE from 'three';
import './index.css';

// const font = 'fonts/helvetiker_regular.typeface.json';
const font = window.fontPath; 
let viewer = new DXFViewer();
let snaps, gui;
let selectingPoints = false;
let selectedPoints = [];
let pointMarkers = [];
let camera;
let html;
let pointSelectionButton;
let resetButton;
let previewSphere = null; 
const basePointSize = 0.1;
let pointSize = basePointSize;
const scaleFactor = 0.01;

export function initDXFViewer(container, file) {
  html = new Boilerplate(container);
  html.onLoad = async (file) => {
    html.three.clear();

    let dxf = await viewer.getFromFile(file, font);
    if (dxf) {
      if (snaps) snaps.clear();
      snaps = new SnapsHelper(dxf, html.three.renderer, html.three.scene, html.three.camera, html.three.controls);

      const layer_names = Object.keys(viewer.layers);
      layer_names.forEach((name) => (viewer.layers[name].entities = []));

      let boundingBox = new THREE.Box3().setFromObject(dxf);
      const maxDimension = Math.max(
        boundingBox.max.x - boundingBox.min.x,
        boundingBox.max.y - boundingBox.min.y,
        boundingBox.max.z - boundingBox.min.z
      );
      pointSize = basePointSize + maxDimension * scaleFactor;

      html.three.scene.add(dxf);
      html.three.centerCamera();
      camera = html.three.camera;

      const guiContainer = document.getElementById('gui-container');
      gui = new GUI({ container: guiContainer });

      const pointSelection = {
        'Seleccionar puntos': () => {
          if (selectedPoints.length >= 3) return;

          selectingPoints = !selectingPoints;

          if (selectingPoints) {
            html.three.renderer.domElement.style.cursor = 'crosshair';
            pointSelectionButton.domElement.style.backgroundColor = '#007BFF';
            pointSelectionButton.domElement.style.color = 'white';
            createPreviewSphere(pointSize);  
          } else {
            html.three.renderer.domElement.style.cursor = 'auto';
            pointSelectionButton.domElement.style.backgroundColor = '';
            pointSelectionButton.domElement.style.color = '';
            removePreviewSphere(); 
          }
        }
      };

      pointSelectionButton = gui.add(pointSelection, 'Seleccionar puntos');
      pointSelectionButton.domElement.style.cursor = 'pointer';

      const pointsCounter = document.getElementById('points-counter');
      resetButton = gui.add({ 
        'Reiniciar puntos': () => resetPoints(pointsCounter) 
      }, 'Reiniciar puntos');
      html.three.renderer.domElement.addEventListener('mousemove', updatePreviewSpherePosition);
      html.three.renderer.domElement.addEventListener('click', (event) => {
        if (selectingPoints && selectedPoints.length < 3) {
          handlePointSelection(event, pointSize, pointsCounter);
        }
      });

      window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && selectingPoints) {
          selectingPoints = false;
          pointSelectionButton.domElement.style.backgroundColor = '';
          pointSelectionButton.domElement.style.color = '';
          html.three.renderer.domElement.style.cursor = 'auto';
          removePreviewSphere(); 
        }
      });

      updatePointsCounter(pointsCounter);
      createHiddenInputs(container); 
    }
  };

  html.init();
  html.loadFile({ target: { files: [file] } });
}

function handlePointSelection(event, pointSize, pointsCounter) {
  const mouse = new THREE.Vector2();
  const rect = event.target.getBoundingClientRect();
  mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
  mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(mouse, camera);

  const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
  const intersectionPoint = new THREE.Vector3();
  raycaster.ray.intersectPlane(plane, intersectionPoint);

  if (intersectionPoint) {
    selectedPoints.push(intersectionPoint.clone());
    addPointMarker(intersectionPoint, selectedPoints.length, pointSize);
    console.log(`Punto ${selectedPoints.length}:`, intersectionPoint);
    updatePointsCounter(pointsCounter);
    window.selectedPointsDXF = selectedPoints.map(point => point.clone());

    // Actualizar los inputs globales
    window[`point${selectedPoints.length}XInput`].value = intersectionPoint.x;
    window[`point${selectedPoints.length}YInput`].value = intersectionPoint.y;

    if (selectedPoints.length === 3) {
      selectingPoints = false;
      pointSelectionButton.domElement.style.backgroundColor = '#CCCCCC';
      pointSelectionButton.domElement.style.color = '#666666';
      pointSelectionButton.domElement.style.pointerEvents = 'none';
      pointSelectionButton.domElement.style.cursor = 'not-allowed';
      html.three.renderer.domElement.style.cursor = 'auto';
      removePreviewSphere();
    }
  }
}


function addPointMarker(point, index, pointSize) {
  const colors = [0x555555, 0xD2691E, 0xFF8C00];
  const outerGeometry = new THREE.SphereGeometry(pointSize, 32, 32);
  const outerMaterial = new THREE.MeshBasicMaterial({
    color: colors[index - 1],
    opacity: 0.6,
    transparent: true
  });
  const outerSphere = new THREE.Mesh(outerGeometry, outerMaterial);
  outerSphere.position.copy(point);

  const innerGeometry = new THREE.SphereGeometry(pointSize * 0.1, 16, 16);
  const innerMaterial = new THREE.MeshBasicMaterial({ color: 0x000000 });
  const innerSphere = new THREE.Mesh(innerGeometry, innerMaterial);
  innerSphere.position.copy(point);

  html.three.scene.add(innerSphere);
  html.three.scene.add(outerSphere);
  pointMarkers.push(innerSphere, outerSphere);
}

function resetPoints(pointsCounter) {
  selectedPoints = [];
  pointMarkers.forEach((marker) => html.three.scene.remove(marker));
  pointMarkers = [];
  
  pointSelectionButton.domElement.style.backgroundColor = '';
  pointSelectionButton.domElement.style.color = '';
  pointSelectionButton.domElement.style.pointerEvents = '';
  pointSelectionButton.domElement.style.cursor = 'pointer';

  html.three.renderer.domElement.style.cursor = 'auto';
  pointSelectionButton.domElement.style.backgroundColor = '';
  pointSelectionButton.domElement.style.color = '';
  selectingPoints = false;
  removePreviewSphere(); 

  for (let i = 1; i <= 3; i++) {
    document.getElementById(`point${i}X`).value = '';
    document.getElementById(`point${i}Y`).value = '';
  }

  updatePointsCounter(pointsCounter); 
}

function updatePointsCounter(pointsCounter) {
  if (pointsCounter) {
    pointsCounter.textContent = `Puntos seleccionados: ${selectedPoints.length}/3`;
  } else {
    console.warn("El elemento 'points-counter' no está disponible.");
  }
}

function createPreviewSphere(size) {
  const geometry = new THREE.SphereGeometry(size, 32, 32);
  const material = new THREE.MeshBasicMaterial({ color: 0x999999, opacity: 0.5, transparent: true });
  previewSphere = new THREE.Mesh(geometry, material);
  html.three.scene.add(previewSphere);
}

function updatePreviewSpherePosition(event) {
  if (selectingPoints && previewSphere) {
    const mouse = new THREE.Vector2();
    const rect = event.target.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse, camera);

    const plane = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
    const intersectionPoint = new THREE.Vector3();
    raycaster.ray.intersectPlane(plane, intersectionPoint);

    previewSphere.position.copy(intersectionPoint);
  }
}

function removePreviewSphere() {
  if (previewSphere) {
    html.three.scene.remove(previewSphere);
    previewSphere.geometry.dispose();
    previewSphere = null;
  }
}

function createHiddenInputs(container) {
  for (let i = 1; i <= 3; i++) {
    const inputX = document.createElement('input');
    inputX.type = 'hidden';
    inputX.id = `point${i}X`;
    container.appendChild(inputX);
    window[`point${i}XInput`] = inputX;

    const inputY = document.createElement('input');
    inputY.type = 'hidden';
    inputY.id = `point${i}Y`;
    container.appendChild(inputY);
    window[`point${i}YInput`] = inputY;
  }
}
